import * as React from 'react';
import {
  getKontodelList,
  getAttributeNames,
} from '../../../../../../../../../../../../Components/Urval/Utils/urvalUtils';
import { IUIUrval } from '../../../../../../../../../../../../Types/UrvalTypes';
import { IClientAttributeModel } from '../../../../../../../../../../../../Types/ClientModelTypes';
import EkonomiskAnalysUrvalSelection from '../UrvalSelection/EkonomiskAnalysUrvalSelection';
import { IKontodelar } from '../../../../../../../Queries/EkonomiskAnalysKontodelarQuery/EkonomiskAnalysKontodelarQuery';
import {
  rangesFromUIUrval,
  updateUrval,
  kontodelarToSuggestions,
  getKontodelarCodeLength,
} from './Utils/SimpleUrvalUtils';
import { equals } from 'ramda';
import { ModelContext } from '../../../../../../../../../../../../Model/ModelContext/ModelContext';
import mergeSort from '../../../../../../../../../../../../Utils/mergeSort';
import { IKontoValues } from '../../../../../../../Queries/EkonomiskAnalysKontobegreppQuery/EkonomiskAnalysKontobegreppQuery';
import {
  IAttributeErrors,
  urvalOutsideUrval,
  combineUrval,
} from '../Utils/UrvalUtils';
import { EkonomiskAnalysContextConsumer } from '../../../../../../Context/EkonomiskAnalysContext';
import { useQuery } from '@apollo/client';
import { ActiveDatabaseContext } from '../../../../../../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { ISuggestion } from '../../../../../../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import leverantorQuery from '../Queries/EkonomiskAnalysLeverantorer.gql';
import {
  EkonomiskAnalysLeverantorerQuery,
  EkonomiskAnalysLeverantorerQueryVariables,
} from '../Queries/_QueryTypes/EkonomiskAnalysLeverantorerQuery';

interface IProps {
  urval: IUIUrval | null;
  kontodelar: IKontodelar;
  kontobegrepp: IKontoValues[];
  setUrval: (newUrval: IUIUrval) => void;
  kodbehorighetsUrval?: IUIUrval;
  autoFocus?: boolean;
  updateReport: (updateOrig?: boolean) => void;
}

interface IInnerProps extends IProps {
  urvalRanges: IRanges;
  kontodelList: IClientAttributeModel[];
  leverantorSuggestion: ISuggestion<string>[];
  grundurvalUrval?: IUIUrval;
}

interface IState {
  /** Mirrors props.urvalRanges, used to know when to update localUrvalRanges */
  urvalRanges: IRanges;
  localUrvalRanges: IRanges;
  attributeErrors: IAttributeErrors[];
  apply: boolean; //sätts när man tabbar ut från fältet, då ska apply köras. Men man kan få in uppdateringar på localUrvalRanges samtidigt så vi måste vänta till nästa update innan den körs.
}

export interface IRanges {
  [key: string]: string;
}

export class EkonomiskAnalysSimpleUrval extends React.PureComponent<
  IInnerProps,
  IState
> {
  static getDerivedStateFromProps(
    nextProps: IInnerProps,
    prevState: IState
  ): IState | null {
    if (!equals(nextProps.urvalRanges, prevState.urvalRanges)) {
      const newUrval = updateUrval(
        nextProps.urvalRanges,
        nextProps.urval,
        nextProps.kontodelList
      );
      return {
        localUrvalRanges: nextProps.urvalRanges,
        apply: false,
        urvalRanges: nextProps.urvalRanges,
        attributeErrors: urvalOutsideUrval(
          newUrval,
          nextProps.grundurvalUrval && nextProps.kodbehorighetsUrval
            ? combineUrval(
                nextProps.kodbehorighetsUrval,
                nextProps.grundurvalUrval
              )
            : nextProps.kodbehorighetsUrval
        ),
      };
    }

    return null;
  }

  constructor(props: IInnerProps) {
    super(props);

    this.state = {
      localUrvalRanges: props.urvalRanges,
      apply: false,
      urvalRanges: props.urvalRanges,
      attributeErrors: [],
    };
  }

  //sätt i state att props.setUrval ska köras
  applyRange = () => () => {
    this.setState(prev => ({ ...prev, apply: true }));
  };

  componentDidUpdate = (prevProps: IProps, prevState: IState) => {
    if (
      this.state.apply &&
      !equals(this.props.urvalRanges, this.state.localUrvalRanges)
    ) {
      const newUrval = updateUrval(
        this.state.localUrvalRanges,
        this.props.urval,
        this.props.kontodelList
      );
      if (newUrval) {
        this.props.setUrval(newUrval);
      }
      this.setState(prev => ({ ...prev, apply: false }));
    }
  };

  updateReport = () => {
    this.applyRange()();
    this.props.updateReport(undefined);
  };

  onUrvalRangeChanged =
    (kontoDel: IClientAttributeModel) =>
    (newValue: string, callback?: () => void) => {
      /*Enums not accepting intervals at the moment*/
      if (kontoDel.DataType === 'Enum') {
        const values = newValue.split(',');
        newValue = values.map(value => value.replace(/-.*/, '')).join(',');
      }
      this.setState(
        oldState => ({
          localUrvalRanges: {
            ...oldState.localUrvalRanges,
            [kontoDel.AttributeName]: newValue,
          },
          attributeErrors: oldState.attributeErrors.filter(
            x => x.name !== kontoDel.AttributeName
          ),
        }),
        callback
      );
    };

  render() {
    return this.props.kontodelList.map((kontoDel, index) => {
      const kontobegreppValues = this.props.kontobegrepp.find(
        x => x.name === kontoDel.AttributeName
      );
      const codeLength = getKontodelarCodeLength(
        this.props.kontodelar,
        kontoDel,
        kontobegreppValues
      );

      const suggestions = kontoDel.AttributeName.includes('Leverantorsnr')
        ? this.props.leverantorSuggestion
        : kontodelarToSuggestions(
            this.props.kontodelar,
            kontoDel,
            kontobegreppValues
          );

      const utanforKodbehorighet = this.state.attributeErrors.find(
        x => x.name === kontoDel.AttributeName
      );

      return (
        <div key={index}>
          <EkonomiskAnalysUrvalSelection
            onBlur={this.applyRange()}
            value={this.state.localUrvalRanges[kontoDel.AttributeName]}
            onChange={this.onUrvalRangeChanged(kontoDel)}
            codeLength={codeLength}
            suggestions={suggestions}
            label={kontoDel.DisplayLabel}
            onUpdate={this.updateReport}
            utanforKodbehorighet={
              utanforKodbehorighet ? utanforKodbehorighet.values : []
            }
            autoFocus={this.props.autoFocus && index === 0}
            isEnumCondition={kontoDel.DataType === 'Enum'}
            maxResult={
              kontoDel.AttributeName.includes('Leverantorsnr') ? 50 : undefined
            }
          />
        </div>
      );
    });
  }
}

/**
 * En visning av dem valda urvalsbegreppen för analysen som enkla värden med autocomplete funktionalitet
 */
const EnhancedEkonomiskAnalysSimpleUrval = (props: IProps) => {
  const { avenyCollectionMap } = React.useContext(ModelContext);
  const { activeDatabase } = React.useContext(ActiveDatabaseContext);
  const urvalRanges = rangesFromUIUrval(props.urval);
  const attributeNames = props.urval
    ? getAttributeNames(props.urval.pages[0].conditionObjects)
    : [];

  const map = getKontodelList(avenyCollectionMap);
  const attributeList = mergeSort(
    map.filter(kontodel => attributeNames.includes(kontodel.AttributeName)),
    (a, b) =>
      attributeNames.indexOf(a.AttributeName) -
      attributeNames.indexOf(b.AttributeName)
  );

  const { data: queryData, loading: queryLoading } = useQuery<
    EkonomiskAnalysLeverantorerQuery,
    EkonomiskAnalysLeverantorerQueryVariables
  >(leverantorQuery, {
    variables: { dbName: activeDatabase ?? '', routine: 2060 },
    skip: activeDatabase == null || !attributeNames.includes('Leverantorsnr'),
  });
  const leverantorSuggestion =
    queryData?.db?.LRLeverantor_All && !queryLoading
      ? queryData?.db?.LRLeverantor_All.map((leverantor: any) => ({
          name: `${leverantor.LEVERANTORSNR} ${leverantor.Namn}`,
          value: leverantor.LEVERANTORSNR,
        }))
      : [];
  return (
    <EkonomiskAnalysContextConsumer>
      {({ grundurvalUrval }) => (
        <EkonomiskAnalysSimpleUrval
          {...props}
          urvalRanges={urvalRanges}
          kontodelList={attributeList}
          grundurvalUrval={grundurvalUrval}
          leverantorSuggestion={leverantorSuggestion}
        />
      )}
    </EkonomiskAnalysContextConsumer>
  );
};

export default EnhancedEkonomiskAnalysSimpleUrval;
